import type { ReactNode } from "react";
import { PosRail, type RailItem } from "@/components/shell/PosRail";
import { PosBottomTabs } from "@/components/shell/PosBottomTabs";
import { CounterTestModeStrip } from "@/components/shell/CounterTestModeStrip";
import { packNav, type PackId } from "@/lib/packs";
import { cn } from "@/lib/utils";

/**
 * POS-WEB-KIT — the shared three-column counter frame every pack screen mounts into:
 * 72px indigo rail · flexible workspace · optional right ledger panel.
 *
 * Matches the mocks' app frame (docs/design/pos-mocks/*.html): full-height, no page scroll;
 * the workspace and the ledger scroll independently.
 */
export function PosShell({
  active,
  nav,
  pack,
  header,
  children,
  aside,
  asideWidth = "w-[384px]",
}: {
  active?: string;
  /** Explicit nav. Omit and pass `pack` to resolve it from the pack registry (preferred). */
  nav?: RailItem[];
  /** Business vertical — resolves the rail nav and the switcher's current state. */
  pack?: PackId;
  /** Sticky workspace header (filters, toggles, search). */
  header?: ReactNode;
  /** Workspace content — scrolls. */
  children: ReactNode;
  /** Right ledger panel (bill, folio, summary). Omit for full-width boards. */
  aside?: ReactNode;
  asideWidth?: string;
}) {

  const items = nav ?? packNav(pack) ?? [];

  /*
   * Three columns on a counter screen, one column on a phone.
   *
   * The frame is 72px of rail plus a 384px ledger before the workspace gets anything, which is more
   * than a 390px phone has in total. Measured before this: at a 606px viewport the rail alone took
   * 355px and the page still scrolled sideways. Every pack's screens mount here, so the phone layout
   * is decided once rather than nine times.
   *
   * On a phone the rail becomes a bottom tab bar, the ledger stops being a column and follows the
   * workspace down the page, and the whole thing scrolls as one. A till is used one-handed; a
   * 384px-wide panel pinned beside a 390px screen is not a layout, it is an apology.
   */
  return (
    <div className="flex h-dvh w-full flex-col bg-bg-base">
      {/* Across the very top while the business has Test mode on for its counters. */}
      <CounterTestModeStrip />
      <div data-testid="pos-page" className="flex min-h-0 w-full flex-1 flex-col md:flex-row">
        {/* Hidden rather than unmounted: the rail keeps its expand/collapse state across a rotation. */}
        <div className="hidden md:flex">
          <PosRail active={active} nav={items} pack={pack} />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-row">
          <main className="flex min-h-0 min-w-0 flex-1 flex-col">
            {header}
            <div className="min-h-0 flex-1 overflow-auto">{children}</div>
          </main>

          {aside && (
            <aside
              className={cn(
                "flex shrink-0 flex-col border-stroke-strong bg-bg-base",
                // Below the workspace on a phone, beside it from md up. `max-h` rather than a fixed
                // height so a long bill scrolls inside its own panel instead of pushing the tabs off.
                "max-h-[45dvh] overflow-auto border-t md:h-full md:max-h-none md:border-l md:border-t-0",
                // The caller's width applies from md up; below it the panel is the full column.
                // NOT `md:${asideWidth}` -- Tailwind scans source text, so a class built by
                // interpolation is never generated and the panel would silently have no width at all.
                // `max-md:` is a real variant and wins under 768px regardless of rule order.
                "max-md:!w-full",
                // And narrower on a tablet: between md and lg the workspace and a 384px ledger are
                // fighting over about 950px, which is what clipped "Register walk-in".
                "max-lg:md:!w-[300px]",
                asideWidth,
              )}
            >
              {aside}
            </aside>
          )}
        </div>

        <PosBottomTabs items={items} active={active} />
      </div>
    </div>
  );
}

/** Standard workspace header bar — bottom rule, comfortable padding, wraps on narrow tablets. */
export function ShellHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  // A COLUMN that also wraps is the one combination this must never produce, and eight screens ask
  // for it by passing `flex-col`. In a wrapping column container the cross size of a flex line is
  // the widest item's max-content, and `items-stretch` stretches the others to THAT rather than to
  // the container -- so one row of stats 422px wide made a 390px header 422px wide, and every row
  // in it was reported outside the viewport. It reads as several broken rows; it is one wrong axis.
  //
  // Decided here rather than at the eight call sites, so the ninth cannot reintroduce it.
  // `cn` is tailwind-merge, so the later `flex-nowrap` replaces the base `flex-wrap`.
  const isColumn = /(^|\s)flex-col(\s|$)/.test(className ?? "");

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 border-b border-stroke-weak px-5 py-3",
        className,
        isColumn && "flex-nowrap",
      )}
    >
      {children}
    </div>
  );
}

/** Page title used in workspace headers — serif, tight. */
export function ShellTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="min-w-0">
      <h1 className="font-text text-lg font-bold leading-tight text-text-strong">{children}</h1>
      {sub && <p className="mt-0.5 text-sm text-text-weak">{sub}</p>}
    </div>
  );
}
